/* eslint-disable complexity */
import { scaleDashboardURL, remotasksURL, outlierURL } from '@scale/frontend-lib/urls';
import { AuthType, AccountType } from '@scale/shared/defaults';
import { userCanAccessOutlier } from '@scale/shared/outlier/login';
import { IUserJson } from '@scale/shared/types/user';
import { DeactivationReason } from '@scale/shared/trustAndSafety/tnsEnforcement/types';

const KINDS: AuthType[] = [
  AuthType.Corp,
  AuthType.RemoAdmin,
  AuthType.BPO,
  AuthType.Worker,
  AuthType.Customer,
  AuthType.Expert,
]; // order matters

interface IAuthStatusSuccess {
  ok: true;
  kind?: AuthType;
}

interface IAuthStatusFail {
  ok: false;
  reason: string;
  kind?: AuthType;
}

type AuthStatus = IAuthStatusSuccess | IAuthStatusFail;

export default function auth(user: IUserJson, kinds?: AuthType[]): AuthStatus {
  // TODO: I believe we look for the email because the endpoint that
  // returns the user under some circumstances returns an empty object
  // when the user is not logged in.
  const isLoggedIn = user && !!user.email;

  if (user && (user as any).banned) {
    return { ok: false, reason: user.deactivationReason ?? 'banned' };
  }
  if (!isLoggedIn) {
    if (user && user.requiresMfa) {
      return { ok: false, reason: 'mfa_required' };
    }
    return { ok: false, reason: 'no_user' };
  }

  if (!kinds || !kinds[0]) return { ok: false, reason: 'no_kind' };

  const authByKind: Record<AuthType, AuthStatus> = {
    corp: authCorp(user),
    bpo: authBPO(user),
    remoadmin: authRemoAdmin(user),
    customer: authCustomer(user),
    worker: authWorker(user),
    expert: authExpert(user),
  };

  // if we find a successful auth for any of the allowed kinds, we
  // return it—order of `kinds` is important because the first match
  // is returned.
  for (const kind of kinds) {
    if (authByKind[kind].ok) return { ...authByKind[kind], kind };
  }

  // no auth was successful for any of the allowed kinds, we pick the
  // first result but maybe we should do something more complicated
  // like prioritize reasons and return the result with the reason
  // with higher priority.
  const defaultAuth = authByKind[kinds[0]];

  // if we find the kind of the user, we include it in the return auth
  // result—the order of `KINDS` is important because the first match
  // is used to determine the kind of user and a single same user can
  // have more than one kind (e.g., 'worker' and 'corp').
  for (const kind of KINDS) {
    if (authByKind[kind].ok) return { ...defaultAuth, kind };
  }

  // couldn't determine the kind of user, just return the auth result.
  return defaultAuth;
}

export function redirectUrlForFailedAuth(
  reason: string,
  userKind: AuthType,
  kinds: AuthType[] = [],
): string {
  if (reason === DeactivationReason.Inactivity && kinds[0] === 'expert')
    return outlierURL('/inactivity');
  if (reason === DeactivationReason.Inactivity && kinds[0] === 'worker')
    return remotasksURL('/inactivity');
  if (reason === 'banned' && kinds[0] === 'expert') return outlierURL('/suspended');
  if (reason === 'banned' && kinds[0] === 'worker') return remotasksURL('/suspended');
  if (reason === 'no_user' && kinds[0] === 'corp') return '/corp/login';
  if (reason === 'no_user' && kinds[0] === 'expert') return '/expert/signup';
  if (reason === 'no_user' && kinds[0] !== 'corp') return '/login';
  if (reason === 'mfa_required') return '/mfa';
  if (reason === 'no_bpo') return '/login';
  if (reason === 'no_remoadmin') return '/login';
  if (reason === 'no_corp') return '/corp/login';
  if (reason === 'forbidden') return '/forbidden';
  if (reason === 'wrong_account_type' && userKind === 'corp') return scaleDashboardURL('/corp?v=1');
  if (reason === 'wrong_account_type' && userKind === 'customer') return scaleDashboardURL('/');
  if (reason === 'wrong_account_type' && userKind === 'worker') return remotasksURL('/dashboard');
  if (reason === 'wrong_account_type' && userKind === 'expert') return outlierURL('/expert');

  // shouldn't be possible, but just in case
  return '/';
}

function authWorker({ accountType, workerStatus, isRemoAdmin }: IUserJson): AuthStatus {
  if (isRemoAdmin) {
    return { ok: true };
  }
  if (![AccountType.Worker, AccountType.Workquester].includes(accountType as AccountType))
    return { ok: false, reason: 'wrong_account_type' };
  if (workerStatus === 'banned') return { ok: false, reason: 'forbidden' };
  return { ok: true };
}

function authExpert(user: IUserJson): AuthStatus {
  if (!userCanAccessOutlier(user)) {
    return { ok: false, reason: 'wrong_account_type' };
  } else if (user.workerStatus === 'banned') {
    return { ok: false, reason: 'forbidden' };
  }

  return { ok: true };
}

function authCustomer({ accountType }: IUserJson): AuthStatus {
  if (accountType !== AccountType.User) return { ok: false, reason: 'wrong_account_type' };
  return { ok: true };
}

function authCorp({ isCorporateUser }: IUserJson): AuthStatus {
  if (!isCorporateUser) return { ok: false, reason: 'no_corp' };
  return { ok: true };
}

function authBPO({ isBPOAdmin }: IUserJson): AuthStatus {
  if (!isBPOAdmin) return { ok: false, reason: 'no_bpo' };
  return { ok: true };
}

function authRemoAdmin({ isRemoAdmin }: IUserJson): AuthStatus {
  if (!isRemoAdmin) return { ok: false, reason: 'no_remoadmin' };
  return { ok: true };
}

export function pageWithTokenAuth(pathname: string): boolean {
  return ['/lidar_debug', '/audit'].includes(pathname);
}

export const maskPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return phoneNumber;
  }
  return phoneNumber.length > 4
    ? `${'*'.repeat(phoneNumber.length - 4)}${phoneNumber.slice(-4)}`
    : phoneNumber;
};

export const maskEmail = (email?: string) => {
  if (!email) {
    return email;
  }
  const emailParts = email?.split('@');
  if (emailParts.length !== 2 || emailParts[0].length <= 6) {
    return email;
  } else {
    const masked = '*'.repeat(emailParts[0].length - 6);
    return masked + emailParts[0].slice(-6) + '@' + emailParts[1];
  }
};
